/**
 * The quote list: products a business wants a quote for. It lives in this browser
 * until the request is sent from the quote page (assets/quote-request.js). Products
 * are kept by handle and variant; titles and prices are fetched fresh where shown.
 */
const STORAGE_KEY = 'aisl-quote-request';

/** Fired on window whenever this page changes the list. */
export const QUOTE_CHANGE_EVENT = 'quote:change';

/**
 * @typedef {object} QuoteItem
 * @property {string} handle
 * @property {number} variantId
 * @property {number} quantity
 */

/**
 * @typedef {object} CustomItem
 * @property {string} product - Brand, model or part number, as the shopper wrote it
 * @property {number} quantity
 * @property {string} note
 */

/**
 * @typedef {object} QuoteList
 * @property {QuoteItem[]} items
 * @property {CustomItem[]} custom
 */

/** @returns {QuoteList} */
export function readList() {
  try {
    const list = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '');
    return {
      items: Array.isArray(list?.items) ? list.items : [],
      custom: Array.isArray(list?.custom) ? list.custom : [],
    };
  } catch {
    return { items: [], custom: [] };
  }
}

/** @param {QuoteList} list */
export function writeList(list) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {
    // Private windows can refuse storage; the page still works for this visit.
  }
  window.dispatchEvent(new CustomEvent(QUOTE_CHANGE_EVENT));
}

export function clearList() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing stored, nothing to clear.
  }
  window.dispatchEvent(new CustomEvent(QUOTE_CHANGE_EVENT));
}

/**
 * @param {QuoteList} list
 * @param {QuoteItem} item
 */
export function addItem(list, item) {
  const existing = list.items.find((line) => line.variantId === item.variantId);
  if (existing) {
    existing.quantity += item.quantity;
  } else {
    list.items.push(item);
  }
}

/** @param {unknown} value */
export function toQuantity(value) {
  const number = Math.floor(Number(value));
  return Number.isFinite(number) && number > 0 ? Math.min(number, 9999) : 1;
}

/** Lines in the list: store products plus described products. */
function countLines() {
  const list = readList();
  return list.items.length + list.custom.filter((line) => line.product?.trim()).length;
}

/**
 * "Add to quote" on a product page or product card. On a product page it takes the
 * variant and quantity picked in the product form named by `data-form`; on a card it
 * adds `data-variant-id` once.
 */
class QuoteButton extends HTMLElement {
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #resetTimer;

  connectedCallback() {
    this.querySelector('button')?.addEventListener('click', this.#add);
  }

  disconnectedCallback() {
    this.querySelector('button')?.removeEventListener('click', this.#add);
    clearTimeout(this.#resetTimer);
  }

  /** @param {Event} event */
  #add = (event) => {
    // Cards sit inside a link; keep the click from opening the product.
    event.preventDefault();
    event.stopPropagation();

    const formId = this.dataset.form ?? '';
    const form = formId ? document.getElementById(formId) : null;
    const variantInput = /** @type {HTMLInputElement | null} */ (form?.querySelector('input[name="id"]'));
    const quantityInput = /** @type {HTMLInputElement | null} */ (
      (formId && document.querySelector(`input[name="quantity"][form="${formId}"]`)) ||
        form?.querySelector('input[name="quantity"]')
    );
    const variantId = Number(variantInput?.value || this.dataset.variantId);
    if (!variantId || !this.dataset.handle) return;

    const list = readList();
    addItem(list, { handle: this.dataset.handle, variantId, quantity: toQuantity(quantityInput?.value ?? 1) });
    writeList(list);

    const status = /** @type {HTMLElement | null} */ (this.querySelector('[data-status]'));
    if (status) status.hidden = false;

    // Cards: show the "added" label for a moment, like the cart button does.
    const label = this.querySelector('[data-label]');
    const button = this.querySelector('button');
    if (label && button && this.dataset.addedLabel) {
      label.textContent = this.dataset.addedLabel;
      button.dataset.quoteAdded = 'true';
      clearTimeout(this.#resetTimer);
      this.#resetTimer = setTimeout(() => {
        label.textContent = this.dataset.label ?? '';
        delete button.dataset.quoteAdded;
      }, 2000);
    }
  };
}

/**
 * Shows how many lines the quote list holds (header icon, menu drawer). The theme
 * morphs the header back to its server HTML, which has no count, so the count is
 * written again whenever the element's contents change.
 */
class QuoteCount extends HTMLElement {
  #observer = new MutationObserver(() => this.#update());

  connectedCallback() {
    window.addEventListener(QUOTE_CHANGE_EVENT, this.#update);
    window.addEventListener('storage', this.#update);
    this.#observer.observe(this, { childList: true, subtree: true, characterData: true, attributes: true });
    this.#update();
  }

  disconnectedCallback() {
    window.removeEventListener(QUOTE_CHANGE_EVENT, this.#update);
    window.removeEventListener('storage', this.#update);
    this.#observer.disconnect();
  }

  #update = () => {
    const count = countLines();
    const bubble = this.querySelector('[data-bubble]');
    const text = this.querySelector('[data-count]');
    if (!bubble || !text) return;

    // Only write what differs, so the observer doesn't keep firing on its own changes.
    const label = count > 99 ? '99+' : String(count);
    if (text.textContent !== label) text.textContent = label;
    if (bubble.classList.contains('visually-hidden') !== (count === 0)) {
      bubble.classList.toggle('visually-hidden', count === 0);
    }
    if (this.classList.contains('header-actions__cart-icon--has-cart') !== count > 0) {
      this.classList.toggle('header-actions__cart-icon--has-cart', count > 0);
    }
  };
}

if (!customElements.get('quote-button')) customElements.define('quote-button', QuoteButton);
if (!customElements.get('quote-count')) customElements.define('quote-count', QuoteCount);
